import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import {
  ArrowLeft,
  Building2,
  CreditCard,
  Check,
  Loader2,
  AlertCircle,
  Globe,
  User,
  Hash,
  Mail
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
axios.defaults.withCredentials = true;
const API = `${BACKEND_URL}/api`;

// Country list for bank country dropdown
const COUNTRIES = [
  "United States", "United Kingdom", "Germany", "France", "Italy", "Spain", 
  "Netherlands", "Belgium", "Austria", "Switzerland", "Portugal", "Ireland",
  "Poland", "Sweden", "Denmark", "Norway", "Finland", "Greece", "Czech Republic",
  "Romania", "Hungary", "Bulgaria", "Croatia", "Slovakia", "Slovenia",
  "Canada", "Australia", "New Zealand", "Japan", "South Korea", "Singapore",
  "Brazil", "Mexico", "Argentina", "Chile", "Colombia", "Peru",
  "South Africa", "Nigeria", "Kenya", "Egypt", "Morocco",
  "India", "Pakistan", "Bangladesh", "Philippines", "Indonesia", "Thailand",
  "Vietnam", "Malaysia", "Turkey", "Israel", "United Arab Emirates", "Saudi Arabia",
  "Russia", "Ukraine", "Other"
].sort();

const PaymentInfoPage = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("bank"); // "bank" or "paypal"
  
  // Bank transfer form
  const [bankForm, setBankForm] = useState({
    full_legal_name: "",
    bank_name: "",
    bank_country: "",
    iban: "",
    swift_bic: "",
    account_number: "",
    routing_number: ""
  });
  const [bankSaving, setBankSaving] = useState(false);
  const [bankError, setBankError] = useState("");
  const [bankSuccess, setBankSuccess] = useState(false);
  const [bankComplete, setBankComplete] = useState(false);
  
  // PayPal form
  const [paypalForm, setPaypalForm] = useState({
    email: ""
  });
  const [paypalSaving, setPaypalSaving] = useState(false);
  const [paypalError, setPaypalError] = useState("");
  const [paypalSuccess, setPaypalSuccess] = useState(false);
  const [paypalComplete, setPaypalComplete] = useState(false);

  useEffect(() => {
    const storedUser = localStorage.getItem("user");
    if (!storedUser) {
      navigate("/login");
      return;
    }
    
    const userData = JSON.parse(storedUser);
    if (userData.role !== "creator") {
      navigate("/app/profile");
      return;
    }
    
    setUser(userData);
    fetchPaymentInfo();
  }, [navigate]);

  const fetchPaymentInfo = async () => {
    try {
      const response = await axios.get(`${API}/public/payment-info`);
      const data = response.data;
      
      // Set bank form
      setBankForm({
        full_legal_name: data.bank_transfer?.full_legal_name || "",
        bank_name: data.bank_transfer?.bank_name || "",
        bank_country: data.bank_transfer?.bank_country || "",
        iban: data.bank_transfer?.iban || "",
        swift_bic: data.bank_transfer?.swift_bic || "",
        account_number: data.bank_transfer?.account_number || "",
        routing_number: data.bank_transfer?.routing_number || ""
      });
      setBankComplete(data.bank_transfer?.is_complete || false);
      
      // Set PayPal form
      setPaypalForm({
        email: data.paypal?.email || ""
      });
      setPaypalComplete(data.paypal?.is_complete || false);
      
    } catch (err) {
      console.error("Failed to fetch payment info:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleBankSubmit = async (e) => {
    e.preventDefault();
    setBankError("");
    setBankSuccess(false);
    setBankSaving(true);
    
    try {
      await axios.put(`${API}/public/payment-info/bank-transfer`, bankForm);
      setBankSuccess(true);
      setBankComplete(true);
      setTimeout(() => setBankSuccess(false), 3000);
    } catch (err) {
      setBankError(err.response?.data?.detail || "Failed to save bank info");
    } finally {
      setBankSaving(false);
    }
  };

  const handlePaypalSubmit = async (e) => {
    e.preventDefault();
    setPaypalError("");
    setPaypalSuccess(false);
    setPaypalSaving(true);
    
    try {
      await axios.put(`${API}/public/payment-info/paypal`, paypalForm);
      setPaypalSuccess(true);
      setPaypalComplete(true);
      setTimeout(() => setPaypalSuccess(false), 3000);
    } catch (err) {
      setPaypalError(err.response?.data?.detail || "Failed to save PayPal info");
    } finally {
      setPaypalSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-cyan-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900" data-testid="payment-info-page">
      {/* Header */}
      <header className="bg-gray-800/50 border-b border-gray-700/50 sticky top-0 z-40 backdrop-blur-xl">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link
                to="/app/profile"
                className="p-2 text-gray-400 hover:text-white hover:bg-gray-700/50 rounded-lg transition-colors"
                data-testid="back-btn"
              >
                <ArrowLeft size={20} />
              </Link>
              <div>
                <h1 className="text-xl font-bold text-white">Payment Information</h1>
                <p className="text-gray-500 text-sm">Manage how you receive your earnings</p>
              </div>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Status Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
          <div className={`p-4 rounded-xl border ${bankComplete ? 'bg-green-500/10 border-green-500/30' : 'bg-gray-800/50 border-gray-700/50'}`}>
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${bankComplete ? 'bg-green-500/20' : 'bg-gray-700/50'}`}>
                <Building2 size={20} className={bankComplete ? 'text-green-400' : 'text-gray-400'} />
              </div>
              <div>
                <p className={`font-medium ${bankComplete ? 'text-green-400' : 'text-white'}`}>Bank Transfer</p>
                <p className="text-sm text-gray-500">{bankComplete ? 'Configured' : 'Not configured'}</p>
              </div>
              {bankComplete && <Check size={20} className="text-green-400 ml-auto" />}
            </div>
          </div>
          
          <div className={`p-4 rounded-xl border ${paypalComplete ? 'bg-green-500/10 border-green-500/30' : 'bg-gray-800/50 border-gray-700/50'}`}>
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${paypalComplete ? 'bg-green-500/20' : 'bg-gray-700/50'}`}>
                <CreditCard size={20} className={paypalComplete ? 'text-green-400' : 'text-gray-400'} />
              </div>
              <div>
                <p className={`font-medium ${paypalComplete ? 'text-green-400' : 'text-white'}`}>PayPal</p>
                <p className="text-sm text-gray-500">{paypalComplete ? 'Configured' : 'Not configured'}</p>
              </div>
              {paypalComplete && <Check size={20} className="text-green-400 ml-auto" />}
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          <button
            onClick={() => setActiveTab("bank")}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
              activeTab === "bank"
                ? "bg-cyan-500/20 text-cyan-400 border border-cyan-500/30"
                : "bg-gray-800/50 text-gray-400 border border-gray-700/50 hover:bg-gray-700/50"
            }`}
            data-testid="tab-bank"
          >
            <Building2 size={18} />
            Bank Transfer
          </button>
          <button
            onClick={() => setActiveTab("paypal")}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
              activeTab === "paypal"
                ? "bg-blue-500/20 text-blue-400 border border-blue-500/30"
                : "bg-gray-800/50 text-gray-400 border border-gray-700/50 hover:bg-gray-700/50"
            }`}
            data-testid="tab-paypal"
          >
            <CreditCard size={18} />
            PayPal
          </button>
        </div>

        {/* Bank Transfer Form */}
        {activeTab === "bank" && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-gray-800/50 rounded-xl border border-gray-700/50 p-6"
          >
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-xl bg-cyan-500/20 flex items-center justify-center">
                <Building2 size={24} className="text-cyan-400" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-white">Bank Transfer Details</h2>
                <p className="text-gray-500 text-sm">Enter your bank account information for receiving payments</p>
              </div>
            </div>

            {bankError && (
              <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg flex items-center gap-2 text-red-400 text-sm">
                <AlertCircle size={16} />
                {bankError}
              </div>
            )}

            {bankSuccess && (
              <div className="mb-4 p-3 bg-green-500/10 border border-green-500/30 rounded-lg flex items-center gap-2 text-green-400 text-sm">
                <Check size={16} />
                Bank transfer info saved successfully!
              </div>
            )}

            <form onSubmit={handleBankSubmit} className="space-y-4">
              {/* Full Legal Name */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Full Legal Name <span className="text-red-400">*</span>
                </label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" size={18} />
                  <input
                    type="text"
                    value={bankForm.full_legal_name}
                    onChange={(e) => setBankForm({ ...bankForm, full_legal_name: e.target.value })}
                    className="w-full pl-10 pr-4 py-3 bg-gray-700/50 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-cyan-500/50 focus:border-cyan-500 transition-all"
                    placeholder="Name as it appears on your bank account"
                    required
                    data-testid="bank-legal-name"
                  />
                </div>
              </div>

              {/* Bank Name & Country */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Bank Name <span className="text-red-400">*</span>
                  </label>
                  <div className="relative">
                    <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" size={18} />
                    <input
                      type="text"
                      value={bankForm.bank_name}
                      onChange={(e) => setBankForm({ ...bankForm, bank_name: e.target.value })}
                      className="w-full pl-10 pr-4 py-3 bg-gray-700/50 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-cyan-500/50 focus:border-cyan-500 transition-all"
                      placeholder="e.g., Chase, Barclays"
                      required
                      data-testid="bank-name"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Bank Country
                  </label>
                  <div className="relative">
                    <Globe className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" size={18} />
                    <select
                      value={bankForm.bank_country}
                      onChange={(e) => setBankForm({ ...bankForm, bank_country: e.target.value })}
                      className="w-full pl-10 pr-4 py-3 bg-gray-700/50 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-cyan-500/50 focus:border-cyan-500 transition-all appearance-none"
                      data-testid="bank-country"
                    >
                      <option value="">Select country</option>
                      {COUNTRIES.map(country => (
                        <option key={country} value={country}>{country}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              {/* IBAN */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  IBAN <span className="text-gray-500 text-xs">(International Bank Account Number)</span>
                </label>
                <div className="relative">
                  <Hash className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" size={18} />
                  <input
                    type="text"
                    value={bankForm.iban}
                    onChange={(e) => setBankForm({ ...bankForm, iban: e.target.value.toUpperCase() })}
                    className="w-full pl-10 pr-4 py-3 bg-gray-700/50 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-cyan-500/50 focus:border-cyan-500 transition-all font-mono"
                    placeholder="e.g., DE89 3704 0044 0532 0130 00"
                    data-testid="bank-iban"
                  />
                </div>
                <p className="text-gray-500 text-xs mt-1">Required for EU/International transfers</p>
              </div>

              {/* SWIFT/BIC */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  SWIFT/BIC Code
                </label>
                <div className="relative">
                  <Hash className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" size={18} />
                  <input
                    type="text"
                    value={bankForm.swift_bic}
                    onChange={(e) => setBankForm({ ...bankForm, swift_bic: e.target.value.toUpperCase() })}
                    className="w-full pl-10 pr-4 py-3 bg-gray-700/50 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-cyan-500/50 focus:border-cyan-500 transition-all font-mono"
                    placeholder="e.g., DEUTDEDB"
                    maxLength={11}
                    data-testid="bank-swift"
                  />
                </div>
              </div>

              {/* US Bank Details */}
              <div className="border-t border-gray-700 pt-4 mt-4">
                <p className="text-gray-400 text-sm mb-4">For US bank accounts (if not using IBAN):</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Account Number
                    </label>
                    <input
                      type="text"
                      value={bankForm.account_number}
                      onChange={(e) => setBankForm({ ...bankForm, account_number: e.target.value })}
                      className="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-cyan-500/50 focus:border-cyan-500 transition-all font-mono"
                      placeholder="Account number"
                      data-testid="bank-account-number"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Routing Number (ABA)
                    </label>
                    <input
                      type="text"
                      value={bankForm.routing_number}
                      onChange={(e) => setBankForm({ ...bankForm, routing_number: e.target.value })}
                      className="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-cyan-500/50 focus:border-cyan-500 transition-all font-mono"
                      placeholder="9-digit routing number"
                      maxLength={9}
                      data-testid="bank-routing-number"
                    />
                  </div>
                </div>
              </div>

              <button
                type="submit"
                disabled={bankSaving}
                className="w-full py-3 bg-gradient-to-r from-cyan-500 to-blue-500 text-white font-medium rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50 flex items-center justify-center gap-2"
                data-testid="save-bank-btn"
              >
                {bankSaving ? (
                  <>
                    <Loader2 size={18} className="animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Check size={18} />
                    Save Bank Details
                  </>
                )}
              </button>
            </form>
          </motion.div>
        )}

        {/* PayPal Form */}
        {activeTab === "paypal" && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-gray-800/50 rounded-xl border border-gray-700/50 p-6"
          >
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-xl bg-blue-500/20 flex items-center justify-center">
                <CreditCard size={24} className="text-blue-400" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-white">PayPal Details</h2>
                <p className="text-gray-500 text-sm">Enter your PayPal email to receive payments</p>
              </div>
            </div>

            {paypalError && (
              <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg flex items-center gap-2 text-red-400 text-sm">
                <AlertCircle size={16} />
                {paypalError}
              </div>
            )}

            {paypalSuccess && (
              <div className="mb-4 p-3 bg-green-500/10 border border-green-500/30 rounded-lg flex items-center gap-2 text-green-400 text-sm">
                <Check size={16} />
                PayPal info saved successfully!
              </div>
            )}

            <form onSubmit={handlePaypalSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  PayPal Email <span className="text-red-400">*</span>
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" size={18} />
                  <input
                    type="email"
                    value={paypalForm.email}
                    onChange={(e) => setPaypalForm({ ...paypalForm, email: e.target.value })}
                    className="w-full pl-10 pr-4 py-3 bg-gray-700/50 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 transition-all"
                    placeholder="your-email@example.com"
                    required
                    data-testid="paypal-email"
                  />
                </div>
                <p className="text-gray-500 text-xs mt-1">Make sure this email is linked to an active PayPal account</p>
              </div>

              <button
                type="submit"
                disabled={paypalSaving}
                className="w-full py-3 bg-gradient-to-r from-blue-500 to-indigo-500 text-white font-medium rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50 flex items-center justify-center gap-2"
                data-testid="save-paypal-btn"
              >
                {paypalSaving ? (
                  <>
                    <Loader2 size={18} className="animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Check size={18} />
                    Save PayPal Details
                  </>
                )}
              </button>
            </form>

            {/* PayPal Info Box */}
            <div className="mt-6 p-4 bg-blue-500/10 border border-blue-500/30 rounded-lg">
              <h3 className="text-blue-400 font-medium mb-2">About PayPal Payments</h3>
              <ul className="text-gray-400 text-sm space-y-1">
                <li>• Payments are typically processed within 24-48 hours</li>
                <li>• PayPal may charge a small receiving fee</li>
                <li>• Ensure your PayPal account can receive payments in your country</li>
              </ul>
            </div>
          </motion.div>
        )}
      </main>
    </div>
  );
};

export default PaymentInfoPage;
